import { useCallback, useEffect, useState } from 'react'
import { AgreementBUInterface } from '../interfaces'
import { getAgreementBU } from '../services/AgreementServices'

/**
 * Trae las unidades de negocio del contrato del cliente activo, desde
 * `GET /Agreement/AgreementBU?AgreementID=`.
 *
 * Es la ÚNICA fuente de las tiendas destino permitidas en toda la aplicación
 * (guía, preenvío, favoritos, import legacy y carga masiva). Antes se leía de
 * `Account/GetAccountSiteBUByAccountSiteID`: son tablas distintas y para un mismo
 * cliente podían no coincidir, así que una tienda permitida se veía en una
 * pantalla y no en otra. AgreementBU es la que el negocio mantiene.
 *
 * Vive en un hook y no en el store persistido a propósito: al estar cacheado
 * por agreementID, cuatro guardas que el slice necesitaba escritas a mano
 * dejan de existir como concepto (descartar respuestas viejas tras cambiar de
 * cliente, limpiar en logout, leer defensivo porque autoMergeLevel1 reemplaza
 * el slice, y saltar cuando no hay contrato). Cambiar de cliente cambia la
 * clave, y ya.
 *
 * Costo conocido: la caché no tiene TTL, así que un cambio del contrato en
 * backoffice se ve recién al recargar la página.
 */

export type ContractDestinationStatus = 'loading' | 'ok' | 'failed'

export interface ContractDestinationBUs {
  /** undefined = todavía no sabemos. [] = sabemos que no hay filas. */
  rows: AgreementBUInterface[] | undefined
  status: ContractDestinationStatus
  /** Reintenta tras un fallo. No hace nada si no hay contrato. */
  retry: () => void
}

const cache = new Map<string, AgreementBUInterface[]>()

/** Sólo para pruebas: la caché es de por vida del proceso. */
export const clearContractDestinationBUsCache = (): void => {
  cache.clear()
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

const hasContract = (agreementID?: string | null): agreementID is string => {
  const id = (agreementID ?? '').trim()
  return id !== '' && id.toLowerCase() !== EMPTY_GUID
}

interface HookState {
  rows: AgreementBUInterface[] | undefined
  status: ContractDestinationStatus
}

const initialState = (agreementID?: string | null): HookState => {
  // Sin contrato no se consulta NUNCA: la ruta sin AgreementID devuelve error y
  // eso dejaría el campo de destino bloqueado a todo cliente de taquilla, que es
  // el camino más común de la aplicación.
  if (!hasContract(agreementID)) return { rows: [], status: 'ok' }

  const cached = cache.get(agreementID)
  if (cached) return { rows: cached, status: 'ok' }

  return { rows: undefined, status: 'loading' }
}

export const useContractDestinationBUs = (
  agreementID?: string | null
): ContractDestinationBUs => {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<HookState>(() => initialState(agreementID))

  useEffect(() => {
    if (!hasContract(agreementID)) {
      setState({ rows: [], status: 'ok' })
      return
    }

    const cached = cache.get(agreementID)
    if (cached) {
      setState({ rows: cached, status: 'ok' })
      return
    }

    let cancelled = false
    setState({ rows: undefined, status: 'loading' })

    getAgreementBU(agreementID).then((response) => {
      if (cancelled) return

      // getAgreementBU atrapa el fallo de red y devuelve didError con model []:
      // ese [] NO puede tomarse por "contrato sin restricciones" (sería mostrar
      // el catálogo entero justo cuando no sabemos nada), por eso se mira didError.
      if (response.didError || !response.model) {
        setState({ rows: undefined, status: 'failed' })
        return
      }

      cache.set(agreementID, response.model)
      setState({ rows: response.model, status: 'ok' })
    })

    return () => {
      cancelled = true
    }
  }, [agreementID, attempt])

  const retry = useCallback(() => {
    setAttempt((previous) => previous + 1)
  }, [])

  return { rows: state.rows, status: state.status, retry }
}
